/**
 * components/email/UsageBar.tsx — storage meter for a mailbox or a domain.
 *
 * Brand palette only: the fill is brand yellow, darkening to navy when the
 * mailbox is nearly full. The percentage is always printed next to the bar
 * so the state is never conveyed by colour alone.
 */
import React from 'react';
import { fmtMb, fmtInt, fmtRelative, fmtAbsolute, MailboxUsage } from './emailApi';

export const usagePct = (used: number | null | undefined, quota: number | null | undefined): number => {
  const u = Number(used) || 0;
  const q = Number(quota) || 0;
  if (q <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((u / q) * 100)));
};

/** Text label for the fullness band — used for aria + the pill. */
export const usageBand = (pct: number): 'ok' | 'high' | 'full' =>
  pct >= 95 ? 'full' : pct >= 80 ? 'high' : 'ok';

const fillClass = (band: ReturnType<typeof usageBand>) =>
  band === 'full' ? 'bg-primary-navy' : band === 'high' ? 'bg-secondary-700' : 'bg-secondary-500';

export const UsageBar: React.FC<{
  usedMb: number | null | undefined;
  quotaMb: number | null | undefined;
  label?: string;
  /** compact = single line (used in table rows). */
  compact?: boolean;
  className?: string;
}> = ({ usedMb, quotaMb, label = 'Storage used', compact = false, className = '' }) => {
  const pct = usagePct(usedMb, quotaMb);
  const band = usageBand(pct);
  const hasQuota = (Number(quotaMb) || 0) > 0;
  return (
    <div className={className}>
      <div className={`flex items-center justify-between gap-2 ${compact ? 'text-xs' : 'text-sm'} text-primary-navy/90 mb-1`}>
        <span>
          <strong>{fmtMb(usedMb)}</strong>
          {hasQuota ? <> of {fmtMb(quotaMb)}</> : null}
        </span>
        <span className="font-mono tabular-nums">
          {hasQuota ? `${pct}%` : '—'}
          {band === 'full' && <span className="ml-1 font-sans font-semibold">· full</span>}
          {band === 'high' && <span className="ml-1 font-sans">· nearly full</span>}
        </span>
      </div>
      <div
        className={`${compact ? 'h-1.5' : 'h-2'} bg-gray-100 rounded overflow-hidden`}
        role="progressbar"
        aria-label={label}
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={hasQuota ? `${fmtMb(usedMb)} of ${fmtMb(quotaMb)} (${pct}%)` : fmtMb(usedMb)}
      >
        <div className={`h-full ${fillClass(band)} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

/** "3d ago · 203.0.113.9 · IMAP" or "never". */
export const LastLogin: React.FC<{ u: Pick<MailboxUsage, 'last_login_at' | 'last_login_ip' | 'last_login_protocol'>; className?: string }> = ({ u, className = '' }) => {
  if (!u.last_login_at) {
    return <span className={className}>Last login: <strong>never</strong></span>;
  }
  return (
    <span className={className} title={fmtAbsolute(u.last_login_at)}>
      Last login: <strong>{fmtRelative(u.last_login_at)}</strong>
      {u.last_login_ip ? <> · <span className="font-mono">{u.last_login_ip}</span></> : null}
      {u.last_login_protocol ? <> · {String(u.last_login_protocol).toUpperCase()}</> : null}
    </span>
  );
};

/** Per-mailbox usage block for a list row. */
export const MailboxUsageRow: React.FC<{ u: MailboxUsage | null | undefined; loading?: boolean }> = ({ u, loading }) => {
  if (!u) {
    return <div className="text-xs text-gray-400 mt-1">{loading ? 'Loading usage…' : 'Usage not available yet.'}</div>;
  }
  return (
    <div className="mt-2 grid grid-cols-1 sm:grid-cols-[minmax(0,260px)_1fr] gap-x-4 gap-y-1 items-end">
      <UsageBar usedMb={u.used_mb} quotaMb={u.quota_mb} compact label={`Storage used by ${u.email}`} />
      <div className="text-xs text-gray-600 flex flex-wrap gap-x-3">
        <span><strong>{fmtInt(u.messages)}</strong> message{Number(u.messages) === 1 ? '' : 's'}</span>
        <LastLogin u={u} />
      </div>
    </div>
  );
};

/** Domain-level total bar (sum used / sum quota). */
export const DomainUsageTotal: React.FC<{
  usedMb: number;
  quotaMb: number;
  mailboxes: number;
  messages?: number;
  title?: string;
  compact?: boolean;
}> = ({ usedMb, quotaMb, mailboxes, messages, title = 'Domain storage', compact = false }) => (
  <div className={compact ? '' : 'bg-white rounded-2xl border border-gray-200 shadow-sm p-5'}>
    <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
      <div className={`${compact ? 'text-xs' : 'text-sm'} font-semibold text-primary-navy`}>{title}</div>
      <div className="text-xs text-gray-500">
        {mailboxes} mailbox{mailboxes === 1 ? '' : 'es'}
        {typeof messages === 'number' ? <> · {fmtInt(messages)} messages</> : null}
      </div>
    </div>
    <UsageBar usedMb={usedMb} quotaMb={quotaMb} label={title} compact={compact} />
  </div>
);

export default UsageBar;
